import { Component, FC, Fragment, useContext, useMemo, useState } from "react";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { AccuracyGapAnalysisChart } from "@app/projects/accuracy-gap-analysis/AccuracyGapAnalysisChart";
import { AccuracyGapAnalysisContext } from "@app/projects/accuracy-gap-analysis/AccuracyGapAnalysisContext";
import { Scorecard } from "@app/projects/accuracy-gap-analysis/Scorecard";
import { AccuracyGapAnalysisStatus } from "@app/projects/enumerators";
import getAGABanner from "@app/shared/AccuracyGapAnalysisBanner";


interface AccuracyGapAnalysisPanelProps {
  onClose?: () => void;
  primaryMembership?: string;
  projectIsDualMembership?: boolean;
  supplementaryMembership?: string;
  status: AccuracyGapAnalysisStatus;
}

interface AccuracyGapAnalysisWrapperProps extends AccuracyGapAnalysisPanelProps {
  projectId: number;
}

const AccuracyGapAnalysisPanel: FC<React.PropsWithChildren<AccuracyGapAnalysisPanelProps>> = (props: AccuracyGapAnalysisPanelProps) => {
  const gapAnalysisContext = useContext(AccuracyGapAnalysisContext);
  const [activeMembership, setActiveMembership] = useState(props.primaryMembership);

  const dataset = useMemo(() => {
    if (activeMembership === props.supplementaryMembership) {
      return gapAnalysisContext.supplementaryConfiguration;
    }
    // can be undefined
    return gapAnalysisContext.primaryConfiguration;
  }, [activeMembership, props.supplementaryMembership, gapAnalysisContext.primaryConfiguration, gapAnalysisContext.supplementaryConfiguration]);

  return (
    <Fragment>
      {getAGABanner(true, props.status)}
      <div className="aga-flyout">
        {props.projectIsDualMembership && props.primaryMembership && props.supplementaryMembership ? (
          <div className="tabs-style-2 mb-5">
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <a className={`nav-link${activeMembership === props.primaryMembership ? " active" : ""}`} role="tab" onClick={() => setActiveMembership(props.primaryMembership)}>{props.primaryMembership}</a>
              </li>
              <li className="nav-item">
                <a className={`nav-link${activeMembership === props.supplementaryMembership ? " active" : ""}`} role="tab" onClick={() => setActiveMembership(props.supplementaryMembership)}>{props.supplementaryMembership}</a>
              </li>
            </ul>
          </div>
        ) : null}
        <div className="aga-scorecard-section">
          {(dataset && dataset.currentScore) ? <Scorecard title="Current" data={dataset.currentScore} /> : <LoadingSpinner />}
          {(dataset && dataset.projectedScore) ? <Scorecard title="Projected" data={dataset.projectedScore} /> : <LoadingSpinner />}
        </div>
        <div className="aga-chart-section">
          {!!dataset?.breakdown && <AccuracyGapAnalysisChart data={dataset.breakdown} />}
        </div>
        <RoleRestricted deniedRoles={[Roles.ACCURACY_LICENSE_TIER_2]}>
          <QESAlert
            type="info"
            persist={false}
            classNames="mt-4 aga-contact-banner"
          >
            Contact us to learn more about your Accuracy Gap Analysis and our available Accuracy options.
          </QESAlert>
          <WizardNavigationBar
            isInAFlyout={true}
            leftSide={<QESButton qesType="secondary" qesTestId="aga-cancel" onClick={props.onClose}>Cancel</QESButton>}
            rightSide={<a className="qes-button qes-button-primary" data-testid="aga-contact-us" href="https://questanalytics.com/accuracy-audit-results-projections/" target="_blank" rel="noreferrer">Contact Us</a>}
          />
        </RoleRestricted>
      </div>
    </Fragment>
  );
}

class AccuracyGapAnalysisPanelWrapper extends Component<AccuracyGapAnalysisWrapperProps> {
  public render() {
    return (
      <RoleRestricted allowedRoles={[Roles.ACCURACY_LICENSE_TIER_1, Roles.ACCURACY_LICENSE_TIER_2]}>
        <AccuracyGapAnalysisPanel {...this.props} />
      </RoleRestricted>
    );
  }
}

export { AccuracyGapAnalysisPanelWrapper as AccuracyGapAnalysisPanel };
